import {useEffect, useState, useRef} from 'react';
import {Link, useOutlet} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import styled from 'styled-components';

import {addMicroservice, clearMicroservices, setMicroservices} from '../../features/microservices.slice';
import Context from './Context';
import _axios from '../../utils/axios';
import _kc from '../../utils/keycloak';
import {USER} from '../../utils/settings';
import validate from '../../utils/validator';
import {handleError} from '../../utils/utils';

import styles from '../../styles/base_components/Navbar.module.scss';

const Content = styled.div`
  &&& {
    ${props => props.displayContent && 'display: block;'}
  }
`;

/**
 * @param {boolean} props.caseChanges
*/
function Navbar(props) {
  const [groups, setGroups] = useState([]);
  const [cases, setCases] = useState([]);

  const [dropdownItems, setDropdownItems] = useState([]);
  const [displayContent, setDisplayContent] = useState(false);
  const outlet = useOutlet();
  const dispatch = useDispatch();

  const ref = useRef();

  function clickDropdownItem(item) {
    return event => {
      if (window.location.pathname.startsWith('/case') && props.caseChanges) {
        if (!window.confirm('You have unsaved changes, are you sure you want to leave the case?')) {
          return;
        }
      }

      switch (item) {
        case 'ADMIN':
          window.location.href = '/admin';
          break;
        case 'GROUP ADMIN':
          window.location.href = '/group-admin';
          break;
        case 'USER':
          window.location.href = '/';
          break;
        default:
          _kc.logout();
          break;
      }
    };
  }

  function onClickContent(event) {
    setDisplayContent(displayContent => !displayContent);
  }

  function onClickLink(event) {
    if (!window.location.pathname.startsWith('/case') || !props.caseChanges) return;

    if (!window.confirm('You have unsaved changes, are you sure you want to leave the case?')) {
      event.preventDefault();
    }
  }

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setDisplayContent(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);

  useEffect(() => {
    dispatch(clearMicroservices());

    _axios.get(USER.MICROSERVICES_PATH)
      .then(response => {
        const {ok, data} = response.data;
        if (typeof ok !== 'number' || !Array.isArray(data)) throw new Error('The microservices from the database do not correspond to our standard.');
        if (ok !== 1) throw new Error('The database path to microservices is currently unavailable.');
        if (data.length === 0) return;

        let newMicroservices = [];
        let error = '';
        for (let microservice of data) {
          let err = validate([
            {value: microservice.id, type: 'db_microservice_id'},
            {value: microservice.fetchers, type: 'db_microservice_fetchers'},
            {value: microservice.status, type: 'db_microservice_status', empty: true},
            {value: microservice.host, type: 'db_microservice_host'},
            {value: microservice.uri, type: 'db_microservice_uri'},
            {value: microservice.version, type: 'db_microservice_version'},
            {value: microservice.author, type: 'db_microservice_author'},
            {value: microservice.name, type: 'db_microservice_name'},
            {value: microservice.created_at, type: 'db_microservice_CreatedAt'},
            {value: microservice.updated_at, type: 'db_microservice_UpdatedAt'}
          ]);
          err = err.filter(e => e !== '');

          if (err.length !== 0) error = error + 'Error at validating ' + microservice.host + ': ' + err.join('. ') + '\n';
          else newMicroservices.push(microservice);
        }
        if (error) throw new Error(error.slice(0, error.length - 2));
        dispatch(setMicroservices(newMicroservices));
      })
      .catch(handleError);
  }, [dispatch]);

  useEffect(() => {
    _axios.get(USER.CASES_PATH + '/all/simple')
      .then(response => {
        const {ok, data} = response.data;
        if (typeof ok !== 'number' || !Array.isArray(data)) throw new Error('The cases from the database do not correspond to our standard.');
        if (ok !== 1) throw new Error('The database path to cases is currently unavailable.');
        if (data.length === 0) return;

        let newCases = [];
        let error = '';
        for (let {id, nodes, edges, name, user, created_at, updated_at} of data) {
          let err = validate([
            {value: nodes, type: 'db_case_nodes', empty: true},
            {value: edges, type: 'db_case_edges', empty: true},
            {value: name, type: 'db_case_name'},
            {value: created_at, type: 'db_case_created_at'},
            {value: updated_at, type: 'db_case_updated_at'}
          ]);
          err = err.filter(e => e !== '');

          if (err.length !== 0) error = error + 'Error at validating ' + name + ': ' + err.join('. ') + '\n';
          else newCases.push({id, name, nodes, edges, created_at, updated_at});
        }
        if (error) throw new Error(error.slice(0, error.length - 2));
        setCases(newCases);
      })
      .catch(handleError);

    const roles = _kc.tokenParsed.realm_access.roles;
    let g = [];
    for (let role of roles) {
      const i = role.search(/_user/);
      if (i === -1) continue;
      g.push(role.substring(0, i));
    }
    setGroups(g);

    const r = roles.filter(role => role.search(/_admin/) !== -1);
    let di = [];
    if (r.find(role => role === 'guests_admin')) di.push('ADMIN');
    if (r.find(role => role !== 'guests_admin')) di.push('GROUP ADMIN');
    di.push('USER');
    di.push('LOGOUT');
    setDropdownItems(di);
  }, []);

  return (
    <Context.Provider value={{
      groups: groups,
      cases: cases,
      setCases: setCases,
      role: USER
    }}>
      <div className={styles.navbar}>
        <Link to='/' onClick={onClickLink} className={styles.logoLink}><div title="Athena" className={styles.logo}></div></Link>
        <Link to='/microservices' onClick={onClickLink}>microservices</Link>
        <Link to='/' onClick={onClickLink}>cases</Link>

        <div className={styles.dropdown} ref={ref} onClick={onClickContent}>
          <div/>
          <Content className={styles.content} displayContent={displayContent}>
            {dropdownItems.map(item => (
              <p key={item} onClick={clickDropdownItem(item)}>{item}</p>
            ))}
          </Content>
        </div>
      </div>

      {outlet}
    </Context.Provider>
  );
}

export default Navbar;